/**
 * chatMarkdown — the shared agent-reply Markdown pipeline for chat bubbles.
 *
 * Extracted from ``MessageBubble`` so the KI-Hofassistent's DS-based bubble
 * (``KiMessageBubble``, #1876) renders agent Markdown through the EXACT same
 * sanitised pipeline as the shared ``MessageBubble`` (Support tab + admin tool),
 * with no duplication. Presentation-only; the sanitisation invariants below are
 * load-bearing.
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components, Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { Link, useInRouterContext } from 'react-router';
import { parseChartBlock } from './chartSpec';
import { ChartBlock } from './ChartBlock';

// Internal routes an agent reply may link into (release-29.3 task 12 §C). The
// assistant references twin objects as ``[<name>](/farm?lense=…&item=…)``; these
// navigate through the SPA router rather than reloading the app.
//
// This is an allow-list of ROUTES, deliberately not derived from the router
// config: it is a security boundary, so it must be the narrow set we intend to
// be linkable from model output — not "whatever routes happen to exist".
const INTERNAL_ROUTE_PREFIXES = ['/farm', '/documents', '/apps'] as const;

// Public origins that ARE this app (#2131). The model absolutizes twin links
// onto a public app origin it knows from training — e.g.
// ``https://app.hofsync.com/farm?lense=…`` — instead of the relative form the
// prompt asks for. When an absolute href targets one of OUR origins we relativize
// it back to an in-app SPA path (and re-run it through the internal-route
// allow-list below, failing closed).
//
// Like INTERNAL_ROUTE_PREFIXES, this is a security boundary, so it is a narrow
// EXPLICIT constant — NOT derived from config — matched by EXACT origin (no
// suffix/wildcard, so "app.hofsync.de.attacker.com" never matches). The set:
//   • apex + www of hofsync.de (primary) and hofsync.com (secondary) — the
//     origins the SPA is actually served at in production, verbatim from the
//     CORS / WebAuthn origin allow-list
//     (infra/kubernetes/overlays/production/kustomization.yaml), routed to the
//     frontend target group by the ALB (infra/terraform/modules/alb);
//   • the app.* aliases — the origin the model was OBSERVED absolutizing onto in
//     the #2131 repro (https://app.hofsync.com/farm?…), also on the backend
//     trusted-host allow-list (backend/tests/unit/core/test_trusted_host.py).
// The current window.location.origin is added at classify time so a self-
// referential absolute link on any deploy host (incl. localhost) also relativizes.
// Broadening this list can never widen the deny-list: an unknown path on any of
// these origins still fails closed to ``blocked`` below.
const KNOWN_APP_ORIGINS = [
    'https://hofsync.de',
    'https://www.hofsync.de',
    'https://hofsync.com',
    'https://www.hofsync.com',
    'https://app.hofsync.de',
    'https://app.hofsync.com',
] as const;

// Characters browsers strip BEFORE resolving a scheme — so "java\nscript:alert(1)"
// is a live javascript: URL to a browser. Strip them before classifying, or the
// check can be walked straight past.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F]/g;

export type ChatHrefKind = 'internal' | 'external' | 'blocked';

export interface ClassifiedHref {
    kind: ChatHrefKind;
    /** The normalised href; only meaningful when kind !== 'blocked'. */
    href: string;
}

function isKnownInternalRoute(path: string): boolean {
    return INTERNAL_ROUTE_PREFIXES.some(
        (prefix) =>
            path === prefix ||
            path.startsWith(`${prefix}/`) ||
            path.startsWith(`${prefix}?`) ||
            path.startsWith(`${prefix}#`),
    );
}

/**
 * Classify an href from (untrusted) agent Markdown.
 *
 * ALLOW: http(s) absolute urls (external), relative paths under a known internal
 * route, and absolute http(s) urls on one of our OWN origins that ALSO resolve to
 * a known internal route (#2131 — relativized to an in-app SPA path).
 * BLOCK: everything else — javascript:/data:/vbscript:/file:/mailto:, protocol-
 * relative ("//evil.example") and backslash-smuggled variants, unknown internal
 * paths (relative OR absolute-same-origin), and anything empty.
 *
 * Deny-by-default: a href only renders as a link when it positively matches an
 * allowed shape, so a scheme nobody thought of is blocked rather than passed.
 */
export function classifyChatHref(raw: string | undefined | null): ClassifiedHref {
    const href = (raw ?? '').replace(CONTROL_CHARS, '').trim();
    if (!href) return { kind: 'blocked', href: '' };

    if (/^https?:\/\//i.test(href)) {
        // An absolute http(s) URL. If it points at one of OUR app origins, the
        // model has absolutized a twin link — relativize it and re-run the
        // internal-route check. This MUST fail CLOSED: a same-origin absolute
        // URL to an unknown path (e.g. https://app.hofsync.de/admin/secrets)
        // becomes ``blocked``, NOT ``external`` — otherwise the normalization
        // would turn the deny-list into an allow-list bypass. Genuine
        // third-party origins keep ``external`` (new tab + noopener).
        const selfOrigin = typeof window !== 'undefined' ? window.location?.origin : undefined;
        try {
            const resolved = new URL(href);
            const isOwnOrigin =
                (KNOWN_APP_ORIGINS as readonly string[]).includes(resolved.origin) ||
                (selfOrigin !== undefined && resolved.origin === selfOrigin);
            if (isOwnOrigin) {
                const path = `${resolved.pathname}${resolved.search}${resolved.hash}`;
                return isKnownInternalRoute(path) ? { kind: 'internal', href: path } : { kind: 'blocked', href: '' };
            }
        } catch {
            // A malformed absolute-looking href must not throw inside the
            // renderer; deny-by-default means it drops rather than renders.
            return { kind: 'blocked', href: '' };
        }
        return { kind: 'external', href };
    }

    // Protocol-relative ("//host") and the backslash variant browsers normalise
    // to it ("/\host") are external in disguise — never internal routes. This runs
    // AFTER the http(s) branch and only ever sees scheme-less input, so a
    // ``new URL()`` above can never silently resolve "//evil.example" as internal.
    if (href.startsWith('//') || href.startsWith('/\\')) return { kind: 'blocked', href: '' };

    if (href.startsWith('/') && isKnownInternalRoute(href)) return { kind: 'internal', href };

    return { kind: 'blocked', href: '' };
}

interface ChatMarkdownLinkProps {
  href?: string;
  children?: React.ReactNode;
}

/**
 * One link inside an agent reply (task 12 §C). The assistant now answers with
 * links to twin objects ("[Rehgraben](/farm?lense=fields&item=…)"):
 *
 * - internal (a known route) → ``<Link>``: SPA navigation, same tab, no reload;
 * - external http(s) → plain anchor, new tab + ``noopener noreferrer``;
 * - blocked → the link is DROPPED and only its text renders, so a hostile href
 *   never reaches the DOM.
 *
 * ``rehype-sanitize`` already strips non-allowed protocols upstream (this
 * component then simply sees no href); ``classifyChatHref`` is the second,
 * explicit layer — and it owns the internal-vs-external decision, which the
 * sanitizer has no opinion on.
 */
const ChatMarkdownLink: React.FC<ChatMarkdownLinkProps> = ({ href, children }) => {
  // Hooks run unconditionally — before any early return.
  const inRouter = useInRouterContext();
  const { kind, href: safeHref } = classifyChatHref(href);

  if (kind === 'blocked') return <>{children}</>;

  if (kind === 'internal') {
    // The bubble mounts inside the app Router on every real surface; the plain
    // anchor is a defensive fallback so a Router-less host degrades rather than
    // throwing.
    return inRouter ? (
      <Link to={safeHref} className="chat-bubble__link">
        {children}
      </Link>
    ) : (
      <a href={safeHref} className="chat-bubble__link">
        {children}
      </a>
    );
  }

  return (
    <a href={safeHref} className="chat-bubble__link" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

// Hard sanitize guard for agent (LLM) Markdown. Stable module-level
// reference so react-markdown isn't handed a fresh array every render.
// INVARIANT: do NOT add ``rehype-raw`` here — react-markdown does not parse
// raw HTML by default, so model output can never become live DOM. If raw
// HTML is ever wanted, ``rehypeRaw`` MUST be ordered BEFORE ``rehypeSanitize``.
const AGENT_MARKDOWN_REHYPE_PLUGINS = [rehypeSanitize];

// #1770: GFM enables tables (plus strikethrough / task-lists) in agent Markdown.
// Runs at the remark/mdast stage — BEFORE rehype-sanitize — so a pipe table
// becomes real table/thead/tbody/tr/th/td nodes that the sanitizer's default
// GitHub allowlist permits. Stable module-level reference, same rationale as
// the rehype array above.
// ``singleTilde: false``: remark-gfm's default (github.com behaviour) pairs
// SINGLE tildes into strikethrough, which would retroactively corrupt numeric
// ranges in existing agent messages ("5~10 kg, letztes Jahr 20~30 kg" →
// 5<del>10 kg, letztes Jahr 20</del>30 kg). Only the GFM-spec double-tilde
// form should strike.
const AGENT_MARKDOWN_REMARK_PLUGINS: Options['remarkPlugins'] = [
  [remarkGfm, { singleTilde: false }],
];

// #1770: wrap each rendered <table> in a scoped overflow-x container so a wide
// (many-column) table scrolls horizontally INSIDE the bubble instead of forcing
// the fixed-width chat panel wider. This custom renderer runs at the LAST
// (hast→JSX) stage, AFTER rehype-sanitize, so the wrapper div is never seen by
// the sanitizer and the sanitized table content passes straight through as
// children. The ``node`` prop is the react-markdown AST node (not a valid
// <table> DOM attr); we drop it via object-rest (``_node`` — underscore keeps
// eslint's no-unused-vars quiet; rest-destructuring siblings are already exempt
// from tsc's ``noUnusedLocals``) so only real DOM attrs spread onto <table>.
const AGENT_MARKDOWN_COMPONENTS: Components = {
  table: ({ node: _node, ...props }) => (
    <div className="chat-bubble__table-wrap">
      <table {...props} />
    </div>
  ),
  // Anchor renderer (task 12 §C). The ``node`` AST prop is dropped (as for
  // ``table`` above) and the rest is forwarded to the real component below.
  a: ({ node: _node, ...props }) => <ChatMarkdownLink {...props} />,
};

// #2219: inline ```chart blocks. The model may answer a small series question
// with a fenced ```chart block carrying strict tiny JSON. ``rehype-sanitize``'s
// default schema keeps ``class="language-chart"`` on ``<code>`` (it matches
// ``/^language-./``), so the fence survives sanitisation UNTOUCHED and, with no
// handler, degrades to exactly today's ``<pre><code class="language-chart">``.
// This handler is OPT-IN (a second stable components object selected by the
// ``charts`` prop) so it lights up ONLY on the KI surfaces — the shared
// ``MessageBubble`` (Support tab + admin tool) keeps the plain code block, per
// the #2219 non-goal. The plugins arrays and the ``a`` security override are
// deliberately UNTOUCHED (no ``rehype-raw``): a chart label only ever renders as
// a React text child / SVG text node, XSS-inert by construction.

/** Minimal shape of the hast nodes we read to detect a ```chart fence. We read
 * the AST node (never React children) — the robust extraction path. */
interface HastFenceNode {
  tagName?: string;
  properties?: { className?: unknown };
  children?: HastFenceNode[];
  value?: string;
}

/**
 * Given a ``<pre>``'s hast node, return the raw fence text IFF its sole child is
 * a ``<code class="language-chart">`` — otherwise null (a normal code block or a
 * non-chart fence). The code node's text carries a trailing newline; trim it.
 * Never throws.
 */
function extractChartRaw(node: unknown): string | null {
  const code = (node as HastFenceNode | undefined)?.children?.[0];
  if (!code || code.tagName !== 'code') return null;
  const className = code.properties?.className;
  const classes = Array.isArray(className)
    ? className
    : typeof className === 'string'
      ? className.split(/\s+/)
      : [];
  if (!classes.includes('language-chart')) return null;
  const text = code.children?.[0]?.value;
  if (typeof text !== 'string') return null;
  return text.replace(/\n$/, '');
}

// Second STABLE module-level components object — never build this per render
// (same react-markdown invariant as the arrays above). Identical to
// ``AGENT_MARKDOWN_COMPONENTS`` except ``pre`` becomes the chart-aware renderer.
const AGENT_MARKDOWN_COMPONENTS_WITH_CHARTS: Components = {
  ...AGENT_MARKDOWN_COMPONENTS,
  // ``ChartAwarePre``: override ``pre`` (NOT ``code`` — a chart <div> inside the
  // default <pre> wrapper is invalid nesting). On a valid spec render the chart;
  // on ANYTHING else fall through to the default <pre>{children}</pre> — exactly
  // today's degraded code block, never an error state (a throw here would take
  // down the whole bubble, so the parser never throws either).
  pre: ({ node, children, ...props }) => {
    const raw = extractChartRaw(node);
    if (raw !== null) {
      const spec = parseChartBlock(raw);
      if (spec) return <ChartBlock spec={spec} />;
    }
    return <pre {...props}>{children}</pre>;
  },
};

/**
 * Renders an agent reply body as sanitised Markdown (## headings, **bold**,
 * - bullets, GFM tables) — NEVER ``dangerouslySetInnerHTML``. The wrapper class
 * carries the bubble's markdown typography + table styling (MessageBubble.css).
 *
 * ``charts`` (#2219) opts the surface into the inline ```chart renderer. It is
 * set ONLY by ``KiMessageBubble`` (the KI panel tab + /agent page); the shared
 * ``MessageBubble`` (Support + admin) leaves it undefined, so those surfaces
 * keep the plain code block.
 */
export const ChatMarkdownBody: React.FC<{ body: string; charts?: boolean }> = ({ body, charts }) => (
  <div className="chat-bubble__body chat-bubble__body--markdown">
    <ReactMarkdown
      remarkPlugins={AGENT_MARKDOWN_REMARK_PLUGINS}
      rehypePlugins={AGENT_MARKDOWN_REHYPE_PLUGINS}
      components={charts ? AGENT_MARKDOWN_COMPONENTS_WITH_CHARTS : AGENT_MARKDOWN_COMPONENTS}
    >
      {body}
    </ReactMarkdown>
  </div>
);
